import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router'
import { useProperty } from '@/hooks/useApi'
import { getPropertyDisplayName } from '@/lib/format-utils'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { PropertyHeader } from '@/components/property/property-header'
import { PropertyPageTabList } from '@/components/property/property-page-tabs'
import { propertyTabPath } from '@/lib/property-routes'
import { FeaturesTab } from '@/components/property/features-tab'
import { WeeklyEmailsTab } from '@/components/property/weekly-emails-tab'
import { ReviewsTab } from '@/components/property/reviews-tab'
import { CommissionCard } from '@/components/property/commission-card'
import { SecurityDepositCard } from '@/components/property/security-deposit-card'
import { CleaningCard } from '@/components/property/cleaning-card'
import { MaTaxCard } from '@/components/property/ma-tax-card'
import { ArchivePropertyCard } from '@/components/property/archive-property-card'
import { AskTab } from '@/components/property/ask-tab'
import { PropertyCalendarTab } from '@/components/property/calendar/property-calendar-tab'

type PageTab = 'calendar' | 'ask' | 'reviews' | 'weekly-emails' | 'settings'

/**
 * format-utils.ts's getPropertyDisplayName declares its own narrower local
 * "shape I need" property type (no `| null` on `cover_photo_url`, etc.) that
 * predates this page's real contract-typed `useProperty` data - extracting
 * its own parameter type and casting the real value against it at the call
 * site keeps that file untouched (out of scope for this conversion).
 */
type PropertyLikeArg = Parameters<typeof getPropertyDisplayName>[0]

export function PropertyDetailsPage() {
  const { propertyId } = useParams<{ propertyId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const rawPageTab = searchParams.get('tab')
  // Calendar is the default: a missing or unknown tab lands there. Every other
  // tab (including `?tab=ask`, which existing links use) is opt-in by name.
  const pageTab: PageTab =
    rawPageTab === 'ask' ||
    rawPageTab === 'reviews' ||
    rawPageTab === 'weekly-emails' ||
    rawPageTab === 'settings'
      ? rawPageTab
      : 'calendar'
  const handlePageTabChange = (value: string) => {
    // Messages lives at its own deep-linkable route
    // (/properties/:propertyId/messages) rather than a ?tab= value here - see
    // docs/archive/messages-redesign-pr-slices.md, Wave 2 step 5.
    if (value === 'messages') {
      navigate(propertyTabPath(propertyId ?? '', 'messages'))
      return
    }
    setSearchParams({ tab: value })
  }

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
    refetch: refetchProperty,
  } = useProperty(propertyId)

  // Old bookmarks/links to ?tab=messages must keep working now that
  // Messages lives at its own route instead of a TabsContent here (Codex
  // PR review) - redirect rather than silently falling back to the Ask
  // tab, which would otherwise look like the link was just broken.
  if (rawPageTab === 'messages') {
    return <Navigate to={`/properties/${propertyId}/messages`} replace />
  }

  const displayName = getPropertyDisplayName(property as PropertyLikeArg, propertyId ?? '')

  const isLoading = isLoadingProperty
  const error = propertyError

  const header = (
    <PropertyHeader
      propertyId={propertyId ?? ''}
      property={property}
      isPropertyLoading={isLoading}
    />
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <LoadingSkeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <LoadingSkeleton className="h-64 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <LoadingSkeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <LoadingSkeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        {header}
        <ErrorState error={error} onRetry={refetchProperty} />
      </div>
    )
  }

  return (
    <div>
      {header}

      {/* Keyed by property: the header's picker switches property on this
          same route (CAP-1034), and tab state such as Ask's selected session
          belongs to one property, so a switch remounts the tabs fresh. */}
      <Tabs key={propertyId} value={pageTab} onValueChange={handlePageTabChange}>
        <PropertyPageTabList />

        <TabsContent value="calendar">
          <PropertyCalendarTab
            propertyId={propertyId ?? ''}
            propertyName={displayName}
            isActive={pageTab === 'calendar'}
          />
        </TabsContent>

        <TabsContent value="ask">
          <AskTab propertyId={propertyId ?? ''} isActive={pageTab === 'ask'} />
        </TabsContent>

        <TabsContent value="reviews">
          <ReviewsTab propertyId={propertyId ?? ''} isActive={pageTab === 'reviews'} />
        </TabsContent>

        <TabsContent value="weekly-emails">
          <WeeklyEmailsTab propertyId={propertyId ?? ''} isActive={pageTab === 'weekly-emails'} />
        </TabsContent>

        <TabsContent value="settings">
          <div className="space-y-4">
            <CommissionCard propertyId={propertyId ?? ''} isActive={pageTab === 'settings'} />
            {/* Above Features deliberately: the amount is set here, and the
                toggle that actually turns deposits on lives in Features below,
                which the card's description points to. */}
            <SecurityDepositCard propertyId={propertyId ?? ''} isActive={pageTab === 'settings'} />
            {/* Cleaning sits with the other per-property operational config. A
                card rather than a sixth top-level tab: it is three fields, and
                Commission and Security deposit already live here. */}
            <CleaningCard
              propertyId={propertyId ?? ''}
              isActive={pageTab === 'settings'}
              archived={property?.archived}
              managedUntil={property?.managed_until}
            />
            {/* Form ROC registration (CAP-548): certificate, operator, town. */}
            <MaTaxCard propertyId={propertyId ?? ''} isActive={pageTab === 'settings'} />
            <FeaturesTab propertyId={propertyId ?? ''} isActive={pageTab === 'settings'} />
            {/* Archive lives last -- a one-way, high-impact action, same
                placement convention as the Danger zone card on user-details.tsx. */}
            <ArchivePropertyCard
              propertyId={propertyId ?? ''}
              archived={property?.archived}
              managedUntil={property?.managed_until}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
